import React, { ImgHTMLAttributes, useEffect, useState, useRef } from 'react';
import { buildResizeUrl } from '../utils/urlBuilder';
import { ImageTransformOptions, ResponsiveImageProps } from '../types';
import { getRescaleConfig } from '../utils/config';
import useResponsiveImage from '../hooks/useResponsiveImage';

type ResponsiveImageComponentProps = ResponsiveImageProps & 
  Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'srcSet' | 'sizes'>;

/**
 * Componente para mostrar imágenes responsivas optimizadas con rescale
 */
export const ResponsiveImage: React.FC<ResponsiveImageComponentProps> = ({
  src,
  alt,
  transformOptions = {},
  responsive = true,
  fallbackWidth,
  placeholderSize,
  className = '',
  style = {},
  fetchPriority,
  sizes,
  loading = 'lazy',
  preset,
  ...imgProps
}) => {
  const config = getRescaleConfig();
  const imgRef = useRef<HTMLImageElement>(null);
  
  // Aplicar presets si es necesario
  let finalOptions: ImageTransformOptions = { ...transformOptions };
  
  if (preset && config.presets[preset]) {
    finalOptions = {
      ...config.presets[preset],
      ...finalOptions // Las opciones específicas tienen prioridad sobre el preset
    };
  }
  
  // Si no se especificó formato, usar el por defecto
  if (!finalOptions.format) {
    finalOptions.format = config.defaultFormat;
  }
  
  // Si no se especificó calidad, usar la por defecto
  if (!finalOptions.quality) {
    finalOptions.quality = config.defaultQuality;
  }
  
  // Determinar ancho fallback basado en preset o valor por defecto
  const defaultFallbackWidth = preset ? 
    (config.presets[preset]?.width || config.presets.medium.width) : 
    config.presets.medium.width;
  
  const actualFallbackWidth = fallbackWidth || defaultFallbackWidth;
  
  // Estado para la transición de imagen
  const [loaded, setLoaded] = useState(false);
  
  // Si la imagen es responsiva, usar el hook para obtener los datos
  const { isLoading, error, data } = useResponsiveImage({
    src,
    options: finalOptions,
    fallbackWidth: actualFallbackWidth,
    placeholderSize
  });
  
  // Resetear el estado de carga cuando cambia la fuente
  useEffect(() => {
    setLoaded(false);
  }, [src]);
  
  // Comprobar si la imagen ya está cargada en caché del navegador
  useEffect(() => {
    if (data && imgRef.current && imgRef.current.complete) {
      setLoaded(true);
    }
  }, [data, imgRef.current]);
  
  // Para imágenes no responsivas o cuando hay error en la carga de datos responsivos
  if (!responsive || (error && !data)) {
    const simpleImageUrl = buildResizeUrl(src, {
      ...finalOptions,
      width: actualFallbackWidth
    });
    
    return (
      <img
        src={simpleImageUrl}
        alt={alt}
        className={className}
        style={style}
        loading={loading}
        fetchPriority={fetchPriority}
        {...imgProps}
      />
    );
  }
  
  // Mientras se cargan los datos responsivos
  if (isLoading && !data) {
    // Mostrar un placeholder o un skeleton mientras carga
    return (
      <div 
        className={`${className} bg-gray-200 animate-pulse`} 
        style={{ 
          ...style,
          height: style.height || '100%',
          width: style.width || '100%'
        }}
        aria-label={alt}
        role="img"
      />
    );
  }
  
  // Si tenemos datos responsivos
  if (data) {
    const { srcSet, src: fallbackSrc, placeholderSrc } = data;
    
    // Manejar transición de imagen
    const handleImageLoaded = () => {
      setLoaded(true);
    };
    
    return (
      <div className="relative overflow-hidden" style={style}>
        {/* Imagen de baja resolución que se muestra primero (efecto blur-up) */}
        {!loaded && (
          <img
            src={placeholderSrc}
            alt=""
            className={`${className} absolute inset-0 w-full h-full object-cover transition-opacity duration-500 blur-sm`}
            style={{ opacity: loaded ? 0 : 1 }}
            aria-hidden="true"
          />
        )}
        
        {/* Imagen principal responsiva */}
        <img
          ref={imgRef}
          src={fallbackSrc}
          srcSet={srcSet}
          sizes={sizes || config.defaultSizes}
          alt={alt}
          className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500`}
          loading={loading}
          fetchPriority={fetchPriority}
          onLoad={handleImageLoaded}
          decoding="async"
          {...imgProps}
        />
      </div>
    );
  }
  
  // Fallback final por si algo falla
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      fetchPriority={fetchPriority}
      {...imgProps}
    />
  );
};

export default ResponsiveImage; 